import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, X, UploadSimple, DownloadSimple, ClockClockwise } from "@phosphor-icons/react";

const STATUS_COLOR = {
    draft: "bg-secondary text-secondary-foreground",
    submitted: "bg-blue-100 text-blue-900 border border-blue-300",
    under_review: "bg-amber-100 text-amber-900 border border-amber-400",
    needs_info: "bg-orange-100 text-orange-900 border border-orange-400",
    approved: "bg-green-100 text-green-900 border border-green-400",
    rejected: "bg-primary text-primary-foreground",
    issued: "bg-black text-white",
};

const CSV_HEADER = "full_name,national_id,phone,email,vin,make,model,year,color,body_type,governorate,engine_number,fuel_type,notes";
const CSV_SAMPLE = `${CSV_HEADER}
Alex Sample,01-1234567,+10005550100,alex@example.com,1HGCM82633A123456,Toyota,Corolla,2019,White,sedan,A,ENG-4567,petrol,Imported`;

export default function Applications() {
    const { t } = useI18n();
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [params, setParams] = useSearchParams();
    const [importOpen, setImportOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const gov = params.get("gov");
    const canImport = ["registrar", "super_admin"].includes(user?.role);

    const load = () => {
        const p = {};
        if (q) p.q = q;
        if (gov) p.gov = gov;
        return api.get("/applications", { params: p }).then((r) => setItems(r.data.applications));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [gov]);

    return (
        <div className="space-y-6" data-testid="applications-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· REGISTRATION</div>
                    <h1 className="text-3xl font-bold tracking-tight mt-1">{t("applications")}</h1>
                </div>
                <div className="flex gap-2">
                    {canImport && (
                        <>
                            <Button variant="ghost" onClick={() => setHistoryOpen(true)} className="rounded-sm" data-testid="btn-import-history">
                                <ClockClockwise size={16} weight="bold" className="me-1" />{t("importHistory") || "History"}
                            </Button>
                            <Button variant="outline" onClick={() => setImportOpen(true)} className="rounded-sm" data-testid="btn-import-csv">
                                <UploadSimple size={16} weight="bold" className="me-1" />{t("importCSV") || "Import CSV"}
                            </Button>
                        </>
                    )}
                    <Link to="/app/applications/new">
                        <Button className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="btn-new-application">
                            <Plus size={16} weight="bold" className="me-1" />{t("newApplication")}
                        </Button>
                    </Link>
                </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
                <Input placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="max-w-md" data-testid="applications-search" />
                <Button variant="secondary" onClick={load} data-testid="applications-search-btn"><MagnifyingGlass size={16} /></Button>
                {gov && (
                    <Badge className="bg-primary/10 text-primary border border-primary/30 rounded-sm uppercase tracking-widest gap-1" data-testid="gov-filter-badge">
                        {t("region")}: {gov}
                        <button onClick={() => setParams({})} className="ms-1" data-testid="gov-filter-clear"><X size={12} weight="bold" /></button>
                    </Badge>
                )}
            </div>
            <Card className="rounded-sm overflow-hidden border-border">
                <table className="w-full data-grid text-sm">
                    <thead className="bg-secondary text-xs uppercase tracking-widest">
                        <tr>
                            <th className="text-start">{t("reference")}</th>
                            <th className="text-start">{t("ownerName")}</th>
                            <th className="text-start">{t("vin")}</th>
                            <th className="text-start">{t("status")}</th>
                            <th className="text-start">{t("createdAt")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-10">{t("noData")}</td></tr>}
                        {items.map((a) => (
                            <tr key={a.id} className="border-t border-border hover:bg-secondary/40" data-testid={`app-row-${a.id}`}>
                                <td className="mono font-medium">{a.reference}</td>
                                <td>{a.owner?.full_name}</td>
                                <td className="mono">{a.vehicle?.vin}</td>
                                <td><Badge className={`${STATUS_COLOR[a.status] || ""} rounded-sm uppercase tracking-widest text-[10px]`}>{a.status}</Badge></td>
                                <td className="mono text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</td>
                                <td className="text-end">
                                    <Link to={`/app/applications/${a.id}`}>
                                        <Button size="sm" variant="ghost" data-testid={`app-view-${a.id}`}>{t("view")}</Button>
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <CsvImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
            <CsvHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} />
        </div>
    );
}

function CsvHistoryDialog({ open, onClose }) {
    const { t } = useI18n();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/csv-imports/history")
            .then((r) => setItems(r.data.imports || []))
            .catch(() => toast.error("Failed to load history"))
            .finally(() => setLoading(false));
    }, [open]);
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-3xl" data-testid="csv-history-dialog">
                <DialogHeader>
                    <DialogTitle className="uppercase tracking-widest text-xs">{t("csvHistoryTitle")}</DialogTitle>
                    <DialogDescription className="text-xs text-muted-foreground">Last 50 bulk imports · click a row to see which lines were skipped.</DialogDescription>
                </DialogHeader>
                <div className="max-h-96 overflow-auto" data-testid="csv-history-body">
                    {loading && <div className="text-xs text-muted-foreground py-6 text-center">{t("loading")}</div>}
                    {!loading && items.length === 0 && (
                        <div className="text-xs text-muted-foreground py-6 text-center">{t("csvNoImports")}</div>
                    )}
                    {!loading && items.length > 0 && (
                        <table className="w-full data-grid text-xs">
                            <thead className="bg-secondary text-[10px] uppercase tracking-widest">
                                <tr>
                                    <th className="text-start">{t("csvWhen")}</th>
                                    <th className="text-start">{t("csvBy")}</th>
                                    <th className="text-end">{t("csvRows")}</th>
                                    <th className="text-end">{t("csvCreated")}</th>
                                    <th className="text-end">{t("csvSkipped")}</th>
                                    <th className="text-start">{t("csvFirstRef")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((h, i) => (
                                    <ImportHistoryRow key={h.id || i} h={h} idx={i} />
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function ImportHistoryRow({ h, idx }) {
    const [open, setOpen] = useState(false);
    const hasErrs = (h.errors || []).length > 0;
    return (
        <>
            <tr className="border-t border-border hover:bg-secondary/40 cursor-pointer" onClick={() => hasErrs && setOpen((o) => !o)} data-testid={`csv-hist-row-${idx}`}>
                <td className="mono">{new Date(h.ts).toLocaleString()}</td>
                <td className="mono truncate max-w-[12rem]">{h.actor_email}</td>
                <td className="text-end mono">{h.rows_seen ?? "—"}</td>
                <td className="text-end mono text-green-700 font-semibold">{h.created}</td>
                <td className="text-end mono text-primary">{h.errors_count}</td>
                <td className="mono">{h.sample_reference || <span className="text-muted-foreground">—</span>}</td>
            </tr>
            {open && hasErrs && (
                <tr className="bg-secondary/20" data-testid={`csv-hist-errors-${idx}`}>
                    <td colSpan={6} className="p-3">
                        <ul className="mono text-[10px] space-y-0.5 max-h-40 overflow-auto">
                            {(h.errors || []).map((e, j) => <li key={j}>· {e}</li>)}
                        </ul>
                    </td>
                </tr>
            )}
        </>
    );
}

function CsvImportDialog({ open, onClose, onDone }) {
    const { t } = useI18n();
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const onFile = async (f) => {
        if (!f) return;
        const buf = await f.text();
        setText(buf);
    };
    const submit = async () => {
        if (!text.trim()) return;
        setBusy(true); setResult(null);
        try {
            const { data } = await api.post("/applications/import-csv", { text });
            setResult(data);
            if (data.created > 0) toast.success(`Imported ${data.created} application(s)`);
            if (data.errors?.length > 0) toast.error(`${data.errors.length} row(s) skipped`);
            onDone?.();
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Import failed");
        }
        setBusy(false);
    };
    const downloadTemplate = () => {
        const blob = new Blob([CSV_SAMPLE], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "utal-mea-applications-template.csv"; a.click();
        URL.revokeObjectURL(url);
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-3xl" data-testid="csv-import-dialog">
                <DialogHeader>
                    <DialogTitle className="uppercase tracking-widest text-xs">{t("csvBulkTitle")}</DialogTitle>
                    <DialogDescription className="text-xs text-muted-foreground">{t("csvBulkHint")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-xs text-muted-foreground">
                            Required columns: <span className="mono">full_name, national_id, vin, make, model, year, color, body_type, governorate</span> (region code A–H). Max 500 rows.
                        </div>
                        <Button size="sm" variant="ghost" onClick={downloadTemplate} data-testid="csv-download-template">
                            <DownloadSimple size={14} className="me-1" />Template
                        </Button>
                    </div>
                    <div className="flex items-center gap-3">
                        <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} className="text-xs" data-testid="csv-file-input" />
                        <span className="text-[10px] text-muted-foreground">or paste below ↓</span>
                    </div>
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={CSV_HEADER + "\n..."}
                        rows={10}
                        dir="ltr"
                        className="w-full mono text-xs border border-input rounded-sm p-2 bg-background text-left"
                        data-testid="csv-import-textarea"
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => { setText(""); setResult(null); }}>{t("reset")}</Button>
                        <Button onClick={submit} disabled={busy || !text.trim()} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="csv-import-submit">
                            {busy ? t("loading") : t("adminImport")}
                        </Button>
                    </div>
                    {result && (
                        <div className="border border-border rounded-sm p-3 bg-secondary/30 text-xs" data-testid="csv-import-result">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span><b>{result.created}</b> created</span>
                                {result.sample_reference && <span className="mono text-muted-foreground">· first ref: {result.sample_reference}</span>}
                            </div>
                            {result.errors?.length > 0 && (
                                <details className="mt-2">
                                    <summary className="cursor-pointer text-primary">{result.errors.length} rows skipped — click to view</summary>
                                    <ul className="mono mt-2 text-[11px] max-h-40 overflow-auto">
                                        {result.errors.map((e, i) => <li key={i}>· {e}</li>)}
                                    </ul>
                                </details>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
